import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { landlordAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  Building2, DoorOpen, Users, Sparkles, ArrowRight, ArrowLeft,
  ShieldCheck, Crown, CheckCircle, Loader2, Mail, Gift,
} from 'lucide-react';

const ONBOARDING_KEY = (uid) => `udorm_onboarding_v2_${uid}`;

const FEATURE_BULLETS = [
  'Unlimited properties, units & tenants',
  'Lease management with e-acknowledgment',
  'Payment tracking & PDF receipts',
  'In-app messaging with tenants',
  'Reports & PDF exports',
];

export function LandlordOnboarding({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [answers, setAnswers] = useState({
    property_count: '',
    total_units: '',
    current_tenants: '',
    primary_goal: '',
  });

  const totalSteps = 5; // 4 questions + 1 plan selector
  const progress = (step / totalSteps) * 100;

  const setNum = (key, v) =>
    setAnswers((p) => ({ ...p, [key]: v.replace(/\D/g, '') }));

  const markComplete = () => {
    if (user) localStorage.setItem(ONBOARDING_KEY(user.user_id), 'complete');
  };

  const handleClose = () => {
    markComplete();
    onOpenChange(false);
  };

  const startCheckout = async (plan) => {
    setSubmitting(true);
    try {
      const { data } = await landlordAPI.createStripeCheckout(plan, window.location.origin);
      markComplete();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
      setSubmitting(false);
    }
  };

  const subscribeLater = () => {
    markComplete();
    toast.success('Welcome aboard! You can subscribe anytime from the Subscription page.');
    onOpenChange(false);
    navigate('/landlord/subscription');
  };

  const canNext = (() => {
    if (step === 1) return !!answers.property_count;
    if (step === 2) return !!answers.total_units;
    if (step === 3) return !!answers.current_tenants;
    if (step === 4) return !!answers.primary_goal;
    return true;
  })();

  // ----- step renderers -----
  const QuestionShell = ({ icon: Icon, color, title, subtitle, children }) => (
    <div className="space-y-6">
      <div className="text-center">
        <div className={`w-14 h-14 rounded-full ${color} flex items-center justify-center mx-auto mb-3`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-xl font-bold">{title}</h2>
        <p className="text-muted-foreground mt-1.5 text-sm">{subtitle}</p>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );

  const renderStep1 = () => (
    <QuestionShell
      icon={Building2}
      color="bg-[#C85A42]"
      title="Welcome to U-DORM!"
      subtitle="Let's get you set up in 4 quick questions."
    >
      <Label htmlFor="prop">How many properties (boarding houses / dorms) do you manage?</Label>
      <Input
        id="prop"
        type="text"
        inputMode="numeric"
        placeholder="e.g., 2"
        value={answers.property_count}
        onChange={(e) => setNum('property_count', e.target.value)}
        className="text-center text-lg"
        data-testid="onb-property-count"
      />
    </QuestionShell>
  );

  const renderStep2 = () => (
    <QuestionShell
      icon={DoorOpen}
      color="bg-emerald-500"
      title="Total units"
      subtitle="Across all your properties — rooms, beds, or studios."
    >
      <Label htmlFor="units">How many rentable units in total?</Label>
      <Input
        id="units"
        type="text"
        inputMode="numeric"
        placeholder="e.g., 20"
        value={answers.total_units}
        onChange={(e) => setNum('total_units', e.target.value)}
        className="text-center text-lg"
        data-testid="onb-total-units"
      />
    </QuestionShell>
  );

  const renderStep3 = () => (
    <QuestionShell
      icon={Users}
      color="bg-indigo-500"
      title="Active tenants"
      subtitle="Roughly how many tenants are currently living in your properties?"
    >
      <Label htmlFor="ten">Current tenants</Label>
      <Input
        id="ten"
        type="text"
        inputMode="numeric"
        placeholder="e.g., 15"
        value={answers.current_tenants}
        onChange={(e) => setNum('current_tenants', e.target.value)}
        className="text-center text-lg"
        data-testid="onb-current-tenants"
      />
    </QuestionShell>
  );

  const GOALS = [
    { key: 'collect_rent', label: 'Collect rent more reliably' },
    { key: 'organize_leases', label: 'Organize leases & documents' },
    { key: 'communicate', label: 'Communicate with tenants in one place' },
    { key: 'grow', label: 'Grow / add more properties' },
  ];

  const renderStep4 = () => (
    <QuestionShell
      icon={Sparkles}
      color="bg-amber-500"
      title="What's your #1 goal?"
      subtitle="So we can highlight the right features for you on the dashboard."
    >
      <div className="grid grid-cols-1 gap-2">
        {GOALS.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => setAnswers((p) => ({ ...p, primary_goal: g.key }))}
            className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
              answers.primary_goal === g.key
                ? 'border-[#C85A42] bg-[#C85A42]/5 font-medium'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
            }`}
            data-testid={`onb-goal-${g.key}`}
          >
            <span className="text-sm">{g.label}</span>
          </button>
        ))}
      </div>
    </QuestionShell>
  );

  const renderStep5 = () => (
    <div className="space-y-5">
      <div className="text-center">
        <div className="w-14 h-14 rounded-full bg-[#2F4F4F] flex items-center justify-center mx-auto mb-3">
          <Crown className="w-7 h-7 text-white" />
        </div>
        <h2 className="text-xl font-bold">You're ready to start</h2>
        <p className="text-muted-foreground mt-1.5 text-sm">
          One plan, everything included. Pick how you'd like to pay.
        </p>
        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <ShieldCheck className="w-3.5 h-3.5" />
          30-Day Money-Back Guarantee
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Monthly */}
        <Card className="border hover:shadow-md transition-shadow" data-testid="onb-plan-monthly">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monthly</p>
            <p className="text-3xl font-bold text-[#2F4F4F] dark:text-white mt-1">
              $9.99<span className="text-sm font-normal text-muted-foreground">/mo</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">One-time charge · No auto-renew</p>
            <Button
              variant="outline"
              className="w-full mt-3"
              onClick={() => startCheckout('monthly')}
              disabled={submitting}
              data-testid="onb-subscribe-monthly"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe Monthly'}
            </Button>
          </CardContent>
        </Card>

        {/* Annual */}
        <Card className="border-2 border-[#C85A42] relative hover:shadow-md transition-shadow" data-testid="onb-plan-annual">
          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#C85A42] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
            Save ~20%
          </div>
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold text-[#C85A42] uppercase tracking-wide">Annual · Best value</p>
            <p className="text-3xl font-bold text-[#C85A42] mt-1">
              $95<span className="text-sm font-normal text-muted-foreground">/yr</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">~$7.92/mo · +36 loyalty days on 2nd year</p>
            <Button
              className="w-full mt-3 bg-[#C85A42] hover:bg-[#B04A35]"
              onClick={() => startCheckout('annual')}
              disabled={submitting}
              data-testid="onb-subscribe-annual"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe Annually'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
        <p className="font-semibold text-foreground flex items-center gap-1.5 mb-1.5">
          <Gift className="w-3.5 h-3.5 text-[#6B8E6B]" /> What's included
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {FEATURE_BULLETS.map((f) => (
            <li key={f} className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 flex items-center gap-1.5">
          <Mail className="w-3 h-3" />
          Questions? Email{' '}
          <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline ml-0.5">
            info@mindfulux.org
          </a>
        </p>
      </div>

      <Button
        variant="ghost"
        className="w-full text-muted-foreground"
        onClick={subscribeLater}
        data-testid="onb-subscribe-later"
      >
        Skip — I'll subscribe later
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="landlord-onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#C85A42]" />
            Get Started with U-DORM
          </DialogTitle>
          <DialogDescription>
            Step {step} of {totalSteps}
          </DialogDescription>
          <Progress value={progress} className="h-2" />
        </DialogHeader>

        <div className="py-3">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
        </div>

        {step < 5 && (
          <DialogFooter className="flex sm:justify-between gap-2">
            <div>
              {step > 1 && (
                <Button variant="ghost" onClick={() => setStep(step - 1)} data-testid="onb-back">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleClose} data-testid="onb-skip">
                Skip for now
              </Button>
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!canNext}
                data-testid="onb-next"
                className="bg-[#C85A42] hover:bg-[#B04A35]"
              >
                Next <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
